import { parseAspectInList } from '@common/Aspect';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { PlusIcon, TeamIcon } from '@prophecy/icons';
import { BaseColor, theme } from '@prophecy/theme';
import { Badges, Button, Stack, StackItem, Text } from '@prophecy/ui-v3';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useGraphQlQuery } from 'frontend/core/src/data/util';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { IconContainer } from '../../config/styled';
import { AllTeamsQuery, teamAspectKeys, TeamWithAspects } from './api';
import { TeamList } from './TeamList';

const StyledStackItem = styled(StackItem)`
  min-height: 400px;
`;

const StyledStack = styled(Stack)`
  margin-right: ${theme.spaces.x12};
`;

export const Teams = ({ uid }: { uid: string }) => {
  const navigate = useNavigate();
  const globalConf = useGlobalVariables();
  const isTeamCreationEnabled = globalConf.isTeamCreationEnabled;

  const defaultTeams = FROZEN_EMPTY_ARRAY as Record<string, unknown>[];
  const { data: { User: { teams: teamList = defaultTeams } = {} } = {}, isLoading: isTeamListLoading } =
    useGraphQlQuery([Entity.Team, 'allTeams'], AllTeamsQuery, undefined, { uid });

  const teamListWithAspects = useMemo(() => {
    return parseAspectInList<TeamWithAspects>(teamList, teamAspectKeys);
  }, [teamList]);

  const disableTeamCreation = !isTeamCreationEnabled;

  return (
    <StyledStack gap={theme.spaces.x32} height='100%'>
      <Stack direction='horizontal' align='space-between' alignY='center'>
        <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
          <Stack direction='horizontal' gap={theme.spaces.x8} alignY='center'>
            <IconContainer aria-hidden='true'>
              <TeamIcon type='extended' />
            </IconContainer>
            <Text level='lg' weight={theme.fontWeight.medium}>
              Teams
            </Text>
          </Stack>
          <Badges tone={BaseColor.grayBlue} aria-label={`${teamListWithAspects.length} teams`}>
            {teamListWithAspects.length}
          </Badges>
        </Stack>

        <Button
          size='s'
          icon={<PlusIcon type='default' />}
          iconPlacement='left'
          disabled={disableTeamCreation}
          onClick={() => {
            navigate(Private_Routes.Create_Entity.team.getUrl(undefined));
          }}
          data-test-id='team-create-team-button'>
          Create Team
        </Button>
      </Stack>
      <StyledStackItem grow='1'>
        <TeamList teamList={teamListWithAspects} isLoading={isTeamListLoading} />
      </StyledStackItem>
    </StyledStack>
  );
};
